import React, { useEffect, useState } from "react"
import { HttpStatusCode } from "axios";
import { Empty, Modal, Spin, Button, Row, Col, Typography, Flex, notification } from "antd";

import { VisualizarDocumentoHTML } from "@/Documentos/VisualizarDocumentoHTML";
import { DocumentoService, useObterHTMLDoRecibo } from "@/Services/Domain/Documentos/DocumentoService";
import TextArea from "antd/es/input/TextArea";
import { useMutation } from "@tanstack/react-query";
import { Recibo as TypeRecibo } from "@/Models/Financeiro/Titulo/Recibo";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";

export const Recibo: React.FC<{ codigoDoTitulo: number } & ({ privilegio: "administrador"; permitirEditarDescricao?: boolean } | { privilegio: "escola" })> = (
    props
) => {
    const { data: htmlQueryData, ...htmlQuery } = useObterHTMLDoRecibo(
        {
            staleTime: 0,
            retry: false,
        },
        [
            props.codigoDoTitulo,
            {
                doNotNotify: (err) => err.response?.status == HttpStatusCode.NotFound,
            },
        ]
    );

    const atualizarReciboMutation = useMutation({
        mutationFn: (payload: TypeRecibo) => {
            return DocumentoService.atualizarRecibo(props.codigoDoTitulo, payload, {});
        },
        onSuccess() {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso("Recibo atualizado com sucesso."),
            });
        },
    });

    const [html, setHTML] = useState<string | undefined>(undefined);
    const [alterandoDescricao, setAlterandoDescricao] = useState<boolean>(false);
    const [novaDescricao, setNovaDescricao] = useState<string | undefined>(undefined);

    useEffect(() => {
        setHTML(htmlQueryData?.data);
    }, [htmlQueryData]);

    const substituirDescricao = (valor: string) => {
        setNovaDescricao(valor);

        if (html == undefined) return;

        const doc = new DOMParser().parseFromString(html, "text/html");
        const tgt = doc.querySelector("#descricao");

        if (tgt) {
            tgt.innerHTML = valor;
            setHTML(doc.documentElement.innerHTML);
        }
    };

    const atualizarRecibo = () => {
        atualizarReciboMutation
            .mutateAsync({
                descricao: novaDescricao,
            })
            .then(() => {
                setAlterandoDescricao(false);
                setNovaDescricao(undefined);
                htmlQuery.refetch();
            })
            .catch(() => {});
    };

    const voltarDescricaoOriginal = () => {
        setHTML(htmlQueryData?.data);
        setAlterandoDescricao(false);
        setNovaDescricao(undefined);
    };

    return (
        <React.Fragment>
            <Spin spinning={htmlQuery.isFetching}>{html ? <VisualizarDocumentoHTML html={html} /> : <Empty />}</Spin>

            {html != undefined && props.privilegio === "administrador" && props.permitirEditarDescricao && (
                <React.Fragment>
                    {!alterandoDescricao ? (
                        <Row>
                            <Col xs={24} style={{ margin: "8px 0" }}>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        const tgt = new DOMParser().parseFromString(html, "text/html").querySelector("#descricao");
                                        if(tgt != null) setNovaDescricao(tgt.innerHTML.trim());
                                        setAlterandoDescricao(true);
                                    }}
                                >
                                    Alterar Descrição
                                </Button>
                            </Col>
                        </Row>
                    ) : (
                        <Row>
                            <Col xs={24}>
                                <Flex gap={8} vertical>
                                    <Typography.Title level={5} style={{ marginLeft: ".5em", margin: 0 }}>
                                        Nova descrição
                                    </Typography.Title>

                                    <TextArea
                                        value={novaDescricao}
                                        style={{ resize: "none" }}
                                        showCount
                                        maxLength={1000}
                                        onChange={(e) => substituirDescricao(e.target.value)}
                                    />

                                    <Flex gap={8}>
                                        <Button type="primary" onClick={atualizarRecibo}>
                                            Salvar
                                        </Button>

                                        <Button type="default" onClick={voltarDescricaoOriginal}>
                                            Cancelar
                                        </Button>
                                    </Flex>
                                </Flex>
                            </Col>
                        </Row>
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
};


export const ReciboModal: React.FC<{
    reciboProps: React.ComponentProps<typeof Recibo>;
    modalProps?: React.ComponentProps<typeof Modal>;
    buttonOpen?: React.ReactNode;
}> = ({ buttonOpen, ...props }) => {
    const [aberta, setAberta] = useState<boolean>(false);

    return (
        <>
            {(buttonOpen && React.cloneElement(buttonOpen as React.ReactElement<any>, { onClick: () => setAberta(true) })) || (
                <Button type="primary" size="small" onClick={() => setAberta(true)}>
                    Recibo
                </Button>
            )}

            <Modal
                destroyOnClose
                width={"80vw"}
                title="Recibo"
                maskClosable={false}
                open={aberta}
                footer={(_, { OkBtn }) => (
                    <>
                        <OkBtn/>
                    </>
                )}
                {...props.modalProps}
                onOk={() => {
                    setAberta(false);
                }}
                onCancel={() => {
                    setAberta(false);
                }}
            >
                {props.reciboProps.codigoDoTitulo ? <Recibo {...props.reciboProps} /> : <></>}
            </Modal>
        </>
    );
};
